// caminho:
// apps/web/src/views/app-views/quiz/settings/index.js

import React, {
  useCallback,
  useEffect,
  useState,
} from 'react'

import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd'

import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons'

import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import fetch from 'auth/FetchInterceptor'

const {
  Title,
  Text,
  Paragraph,
} = Typography

const {
  TextArea,
} = Input

// ======================================================
// HELPERS
// ======================================================

const getErrorMessage = (
  error,
  fallback,
) => {
  const apiMessage =
    error?.response
      ?.data
      ?.message ||
    error?.message ||
    fallback

  if (
    Array.isArray(
      apiMessage,
    )
  ) {
    return apiMessage.join(
      ', ',
    )
  }

  return apiMessage
}

// ======================================================
// QUIZ SETTINGS
// ======================================================

const QuizSettingsPage = () => {
  // ====================================================
  // URL
  // ====================================================

  const navigate =
    useNavigate()

  const [searchParams] =
    useSearchParams()

  const gameId =
    searchParams.get(
      'gameId',
    )

  // ====================================================
  // STATE
  // ====================================================

  const [
    loading,
    setLoading,
  ] = useState(true)

  const [
    error,
    setError,
  ] = useState(null)

  const [
    data,
    setData,
  ] = useState(null)

  // ====================================================
  // CONFIGURAÇÃO GERAL
  // ====================================================

  const [
    configModalOpen,
    setConfigModalOpen,
  ] = useState(false)

  const [
    savingConfig,
    setSavingConfig,
  ] = useState(false)

  const [
    configForm,
  ] = Form.useForm()

  // ====================================================
  // PERGUNTA
  // ====================================================

  const [
    questionModalOpen,
    setQuestionModalOpen,
  ] = useState(false)

  const [
    editingQuestion,
    setEditingQuestion,
  ] = useState(null)

  const [
    savingQuestion,
    setSavingQuestion,
  ] = useState(false)

  const [
    deletingQuestionId,
    setDeletingQuestionId,
  ] = useState(null)

  const [
    questionForm,
  ] = Form.useForm()

  // ====================================================
  // PERFIL DE RESULTADO
  // ====================================================

  const [
    resultModalOpen,
    setResultModalOpen,
  ] = useState(false)

  const [
    editingResult,
    setEditingResult,
  ] = useState(null)

  const [
    savingResult,
    setSavingResult,
  ] = useState(false)

  const [
    deletingResultId,
    setDeletingResultId,
  ] = useState(null)

  const [
    resultForm,
  ] = Form.useForm()

  // ====================================================
  // CARREGAR QUIZ
  // ====================================================

  const loadQuiz =
    useCallback(
      async () => {
        if (!gameId) {
          setError(
            'Nenhum jogo foi selecionado.',
          )

          setData(null)

          setLoading(
            false,
          )

          return
        }

        try {
          setLoading(
            true,
          )

          setError(
            null,
          )

          const response =
            await fetch.get(
              `/quiz-games/${gameId}`,
            )

          setData(
            response?.data ||
            response,
          )
        } catch (err) {
          console.error(
            '[QUIZ] Erro ao carregar:',
            err,
          )

          setError(
            getErrorMessage(
              err,
              'Não foi possível carregar o Quiz.',
            ),
          )

          setData(
            null,
          )
        } finally {
          setLoading(
            false,
          )
        }
      },
      [
        gameId,
      ],
    )

  // ====================================================
  // CONFIGURAÇÃO GERAL
  // ====================================================

  const handleEditConfig =
    () => {
      const config =
        data?.config

      const game =
        data?.game

      if (
        !config ||
        !game
      ) {
        return
      }

      configForm.resetFields()

      configForm.setFieldsValue({
        // ================================================
        // QUIZ
        // ================================================

        mode:
          config.mode ||
          'SCORE_PROFILE',

        showProgress:
          config.showProgress !==
          false,

        showScore:
          config.showScore ===
          true,

        autoAdvance:
          config.autoAdvance !==
          false,

        // ================================================
        // CAMPOS OBRIGATÓRIOS DO GAME
        // ================================================

        requireName:
          game.requireName ??
          true,

        requirePhone:
          game.requirePhone ??
          false,

        requireEmail:
          game.requireEmail ??
          false,

        requireCpf:
          game.requireCpf ??
          false,

        // ================================================
        // REGRAS DE PARTICIPAÇÃO
        // ================================================

        onePlayPerPhone:
          game.onePlayPerPhone ??
          false,

        onePlayPerEmail:
          game.onePlayPerEmail ??
          false,

        onePlayPerCpf:
          game.onePlayPerCpf ??
          false,

        participationLimitType:
          game.participationLimitType ||
          'NONE',

        participationLimitMinutes:
          game.participationLimitMinutes ??
          60,
      })

      setConfigModalOpen(
        true,
      )
    }

  const handleCloseConfig =
    () => {
      if (
        savingConfig
      ) {
        return
      }

      setConfigModalOpen(
        false,
      )

      configForm.resetFields()
    }

  const handleSaveConfig =
    async () => {
      try {
        const values =
          await configForm.validateFields()

        setSavingConfig(
          true,
        )

        // ==================================================
        // CONFIGURAÇÃO ESPECÍFICA DO QUIZ
        // ==================================================

        await fetch.put(
          `/quiz-games/${gameId}/config`,
          {
            mode:
              values.mode,

            showProgress:
              values.showProgress ===
              true,

            showScore:
              values.showScore ===
              true,

            autoAdvance:
              values.autoAdvance ===
              true,
          },
        )

        // ==================================================
        // CONFIGURAÇÃO DO GAME
        // ==================================================

        const gamePayload = {
          requireName:
            values.requireName ===
            true,

          requirePhone:
            values.requirePhone ===
            true,

          requireEmail:
            values.requireEmail ===
            true,

          requireCpf:
            values.requireCpf ===
            true,

          onePlayPerPhone:
            values.onePlayPerPhone ===
            true,

          onePlayPerEmail:
            values.onePlayPerEmail ===
            true,

          onePlayPerCpf:
            values.onePlayPerCpf ===
            true,

          participationLimitType:
            values.participationLimitType ||
            'NONE',

          participationLimitMinutes:
            values.participationLimitType ===
              'MINUTES'
              ? Number(
                values.participationLimitMinutes ||
                60,
              )
              : null,
        }

        await fetch.patch(
          `/games/${gameId}`,
          gamePayload,
        )

        message.success(
          'Configuração atualizada com sucesso.',
        )

        setConfigModalOpen(
          false,
        )

        await loadQuiz()
      } catch (err) {
        if (
          err?.errorFields
        ) {
          return
        }

        console.error(
          '[QUIZ] Erro ao salvar configuração:',
          err,
        )

        message.error(
          getErrorMessage(
            err,
            'Não foi possível salvar a configuração.',
          ),
        )
      } finally {
        setSavingConfig(
          false,
        )
      }
    }
  // ====================================================
  // NOVA PERGUNTA
  // ====================================================

  const handleNewQuestion =
    () => {
      setEditingQuestion(
        null,
      )

      questionForm.resetFields()

      questionForm.setFieldsValue({
        title: '',
        text: '',
        isActive: true,

        options: [
          {
            text: '',
            points: 1,
            isCorrect: false,
          },
          {
            text: '',
            points: 2,
            isCorrect: false,
          },
          {
            text: '',
            points: 3,
            isCorrect: false,
          },
        ],
      })

      setQuestionModalOpen(
        true,
      )
    }

  // ====================================================
  // EDITAR PERGUNTA
  // ====================================================

  const handleEditQuestion =
    (
      question,
    ) => {
      setEditingQuestion(
        question,
      )

      questionForm.resetFields()

      questionForm.setFieldsValue({
        title:
          question?.title ||
          '',

        text:
          question?.text ||
          '',

        isActive:
          question?.isActive !==
          false,

        options:
          (
            question?.options ||
            []
          ).map(
            (option) => ({
              text:
                option?.text ||
                '',

              points:
                Number(
                  option?.points ??
                  0,
                ),

              isCorrect:
                option?.isCorrect ===
                true,
            }),
          ),
      })

      setQuestionModalOpen(
        true,
      )
    }

  // ====================================================
  // FECHAR PERGUNTA
  // ====================================================

  const handleCloseQuestionModal =
    () => {
      if (
        savingQuestion
      ) {
        return
      }

      setQuestionModalOpen(
        false,
      )

      setEditingQuestion(
        null,
      )

      questionForm.resetFields()
    }

  // ====================================================
  // SALVAR PERGUNTA
  // ====================================================

  const handleSaveQuestion =
    async () => {
      try {
        const values =
          await questionForm.validateFields()

        setSavingQuestion(
          true,
        )

        // ==================================================
        // PAYLOAD BASE
        // ==================================================

        const basePayload = {
          title:
            String(
              values?.title ||
              '',
            ).trim(),

          text:
            String(
              values?.text ||
              '',
            ).trim(),

          isActive:
            values?.isActive !==
            false,
        }

        // ==================================================
        // EDITAR PERGUNTA
        // ==================================================

        if (
          editingQuestion
        ) {
          /*
           * IMPORTANTE:
           *
           * Na edição NÃO enviamos as alternativas.
           *
           * Isso permite alterar:
           * - título
           * - pergunta
           * - ativa/inativa
           *
           * mesmo quando já existem respostas registradas.
           *
           * O backend continua preservando as alternativas
           * usadas nas respostas anteriores.
           */

          await fetch.put(
            `/quiz-games/${gameId}/questions/${editingQuestion.id}`,
            basePayload,
          )

          message.success(
            values?.isActive ===
              false
              ? 'Pergunta desativada com sucesso.'
              : 'Pergunta atualizada com sucesso.',
          )
        } else {
          // ==================================================
          // NOVA PERGUNTA
          // ==================================================

          const options =
            (
              values?.options ||
              []
            ).map(
              (
                option,
                index,
              ) => ({
                label:
                  String.fromCharCode(
                    65 + index,
                  ),

                text:
                  String(
                    option?.text ||
                    '',
                  ).trim(),

                points:
                  Number(
                    option?.points ??
                    0,
                  ),

                isCorrect:
                  option?.isCorrect ===
                  true,

                sortOrder:
                  index,

                isActive:
                  true,
              }),
            )

          if (
            options.length <
            2
          ) {
            message.error(
              'A pergunta precisa ter pelo menos 2 alternativas.',
            )

            return
          }

          await fetch.post(
            `/quiz-games/${gameId}/questions`,
            {
              ...basePayload,
              options,
            },
          )

          message.success(
            'Pergunta criada com sucesso.',
          )
        }

        // ==================================================
        // FINALIZA
        // ==================================================

        setQuestionModalOpen(
          false,
        )

        setEditingQuestion(
          null,
        )

        questionForm.resetFields()

        await loadQuiz()
      } catch (err) {
        // Erro de validação do Ant Design.
        if (
          err?.errorFields
        ) {
          return
        }

        console.error(
          '[QUIZ] Erro ao salvar pergunta:',
          err,
        )

        message.error(
          getErrorMessage(
            err,
            'Não foi possível salvar a pergunta.',
          ),
        )
      } finally {
        setSavingQuestion(
          false,
        )
      }
    }

  // ====================================================
  // EXCLUIR PERGUNTA
  // ====================================================

  const handleDeleteQuestion =
    async (
      question,
    ) => {
      if (
        !question?.id
      ) {
        return
      }

      try {
        setDeletingQuestionId(
          question.id,
        )

        await fetch.delete(
          `/quiz-games/${gameId}/questions/${question.id}`,
        )

        message.success(
          'Pergunta excluída com sucesso.',
        )

        await loadQuiz()
      } catch (err) {
        console.error(
          '[QUIZ] Erro ao excluir pergunta:',
          err,
        )

        message.error(
          getErrorMessage(
            err,
            'Não foi possível excluir a pergunta.',
          ),
        )
      } finally {
        setDeletingQuestionId(
          null,
        )
      }
    }

  // ====================================================
  // NOVO PERFIL
  // ====================================================

  const handleNewResult =
    () => {
      setEditingResult(
        null,
      )

      resultForm.resetFields()

      resultForm.setFieldsValue({
        title: '',
        subtitle: '',
        minScore: 0,
        maxScore: 0,
        description: '',
        recommendation: '',
        isActive: true,
      })

      setResultModalOpen(
        true,
      )
    }

  // ====================================================
  // EDITAR PERFIL
  // ====================================================

  const handleEditResult =
    (
      result,
    ) => {
      setEditingResult(
        result,
      )

      resultForm.resetFields()

      resultForm.setFieldsValue({
        title:
          result?.title ||
          '',

        subtitle:
          result?.subtitle ||
          '',

        minScore:
          Number(
            result?.minScore ??
            0,
          ),

        maxScore:
          Number(
            result?.maxScore ??
            0,
          ),

        description:
          result?.description ||
          '',

        recommendation:
          result?.recommendation ||
          '',

        isActive:
          result?.isActive !==
          false,
      })

      setResultModalOpen(
        true,
      )
    }

  // ====================================================
  // FECHAR PERFIL
  // ====================================================

  const handleCloseResultModal =
    () => {
      if (
        savingResult
      ) {
        return
      }

      setResultModalOpen(
        false,
      )

      setEditingResult(
        null,
      )

      resultForm.resetFields()
    }

  // ====================================================
  // SALVAR PERFIL
  // ====================================================

  const handleSaveResult =
    async () => {
      try {
        const values =
          await resultForm.validateFields()

        const minScore =
          Number(
            values.minScore,
          )

        const maxScore =
          Number(
            values.maxScore,
          )

        if (
          maxScore <
          minScore
        ) {
          message.error(
            'A pontuação máxima não pode ser menor que a mínima.',
          )

          return
        }

        setSavingResult(
          true,
        )

        const payload = {
          title:
            String(
              values.title ||
              '',
            ).trim(),

          subtitle:
            String(
              values.subtitle ||
              '',
            ).trim(),

          minScore,

          maxScore,

          description:
            String(
              values.description ||
              '',
            ).trim(),

          recommendation:
            String(
              values.recommendation ||
              '',
            ).trim(),

          isActive:
            values.isActive !==
            false,
        }

        if (
          editingResult
        ) {
          await fetch.put(
            `/quiz-games/${gameId}/results/${editingResult.id}`,
            payload,
          )

          message.success(
            'Perfil atualizado com sucesso.',
          )
        } else {
          await fetch.post(
            `/quiz-games/${gameId}/results`,
            payload,
          )

          message.success(
            'Perfil criado com sucesso.',
          )
        }

        setResultModalOpen(
          false,
        )

        setEditingResult(
          null,
        )

        resultForm.resetFields()

        await loadQuiz()
      } catch (err) {
        if (
          err?.errorFields
        ) {
          return
        }

        console.error(
          '[QUIZ] Erro ao salvar perfil:',
          err,
        )

        message.error(
          getErrorMessage(
            err,
            'Não foi possível salvar o perfil.',
          ),
        )
      } finally {
        setSavingResult(
          false,
        )
      }
    }

  // ====================================================
  // EXCLUIR PERFIL
  // ====================================================

  const handleDeleteResult =
    async (
      result,
    ) => {
      if (
        !result?.id
      ) {
        return
      }

      try {
        setDeletingResultId(
          result.id,
        )

        await fetch.delete(
          `/quiz-games/${gameId}/results/${result.id}`,
        )

        message.success(
          'Perfil excluído com sucesso.',
        )

        await loadQuiz()
      } catch (err) {
        console.error(
          '[QUIZ] Erro ao excluir perfil:',
          err,
        )

        message.error(
          getErrorMessage(
            err,
            'Não foi possível excluir o perfil.',
          ),
        )
      } finally {
        setDeletingResultId(
          null,
        )
      }
    }

  // ====================================================
  // INIT
  // ====================================================

  useEffect(
    () => {
      loadQuiz()
    },
    [
      loadQuiz,
    ],
  )

  // ====================================================
  // LOADING
  // ====================================================

  if (loading) {
    return (
      <div
        style={{
          minHeight:
            400,

          display:
            'flex',

          alignItems:
            'center',

          justifyContent:
            'center',
        }}
      >
        <Spin
          size="large"
        />
      </div>
    )
  }

  // ====================================================
  // ERRO
  // ====================================================

  if (error) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message="Erro ao carregar Quiz"
          description={
            error
          }
        />

        <Button
          type="primary"
          icon={
            <ReloadOutlined />
          }
          onClick={
            loadQuiz
          }
          style={{
            marginTop:
              16,
          }}
        >
          Tentar novamente
        </Button>
      </Card>
    )
  }

  // ====================================================
  // DADOS
  // ====================================================

  const game =
    data?.game

  const config =
    data?.config

  const questions =
    config?.questions ||
    []

  const results =
    config?.results ||
    []

  // ====================================================
  // SEM CONFIGURAÇÃO
  // ====================================================

  if (
    !game ||
    !config
  ) {
    return (
      <Card>
        <Empty
          description="Configuração do Quiz não encontrada."
        />
      </Card>
    )
  }

  // ====================================================
  // PONTUAÇÃO POSSÍVEL
  // ====================================================

  let minScore = 0
  let maxScore = 0

  questions.forEach(
    (question) => {
      const options =
        question?.options ||
        []

      if (
        options.length ===
        0
      ) {
        return
      }

      const points =
        options.map(
          (option) =>
            Number(
              option?.points ||
              0,
            ),
        )

      minScore +=
        Math.min(
          ...points,
        )

      maxScore +=
        Math.max(
          ...points,
        )
    },
  )

  // ====================================================
  // VIEW
  // ====================================================

  return (
    <div>
      {/* ==================================================
          CABEÇALHO
      ================================================== */}

      <Card
        style={{
          marginBottom:
            24,
        }}
      >
        <Row
          justify="space-between"
          align="middle"
          gutter={[
            16,
            16,
          ]}
        >
          <Col>
            <Title
              level={2}
              style={{
                marginBottom:
                  4,
              }}
            >
              {
                game.name
              }
            </Title>

            <Text
              type="secondary"
            >
              {game.slug}
            </Text>
          </Col>

          <Col>
            <Space
              wrap
            >
              <Tag
                color={
                  game.status ===
                    'ACTIVE'
                    ? 'green'
                    : 'default'
                }
              >
                {
                  game.status ===
                    'ACTIVE'
                    ? 'ATIVO'
                    : game.status
                }
              </Tag>

              <Tag
                color="blue"
              >
                {
                  config.mode
                }
              </Tag>

              <Button
                icon={
                  <ReloadOutlined />
                }
                onClick={
                  loadQuiz
                }
              >
                Atualizar
              </Button>
              <Button
                type="primary"
                icon={
                  <SettingOutlined />
                }
                onClick={() =>
                  navigate(
                    `/app/quiz/appearance?gameId=${gameId}`,
                  )
                }
              >
                Personalização
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ==================================================
          RESUMO
      ================================================== */}

      <Row
        gutter={[
          16,
          16,
        ]}
        style={{
          marginBottom:
            24,
        }}
      >
        <Col
          xs={24}
          md={8}
        >
          <Card>
            <Text
              type="secondary"
            >
              Perguntas
            </Text>

            <Title
              level={2}
              style={{
                margin:
                  0,
              }}
            >
              {
                questions.length
              }
            </Title>
          </Card>
        </Col>

        <Col
          xs={24}
          md={8}
        >
          <Card>
            <Text
              type="secondary"
            >
              Perfis de resultado
            </Text>

            <Title
              level={2}
              style={{
                margin:
                  0,
              }}
            >
              {
                results.length
              }
            </Title>
          </Card>
        </Col>

        <Col
          xs={24}
          md={8}
        >
          <Card>
            <Text
              type="secondary"
            >
              Pontuação possível
            </Text>

            <Title
              level={2}
              style={{
                margin:
                  0,
              }}
            >
              {
                questions.length >
                  0
                  ? `${minScore}–${maxScore}`
                  : '—'
              }
            </Title>
          </Card>
        </Col>
      </Row>

      {/* ==================================================
          CONFIGURAÇÃO GERAL
      ================================================== */}

      <Card
        title="Configuração Geral"
        extra={
          <Button
            icon={
              <SettingOutlined />
            }
            onClick={
              handleEditConfig
            }
          >
            Editar configuração
          </Button>
        }
        style={{
          marginBottom:
            24,
        }}
      >
        <Row
          gutter={[
            24,
            16,
          ]}
        >
          <Col
            xs={24}
            sm={12}
            lg={6}
          >
            <Text
              type="secondary"
            >
              Modo
            </Text>

            <div>
              <Tag
                color="blue"
              >
                {
                  config.mode
                }
              </Tag>
            </div>
          </Col>

          <Col
            xs={24}
            sm={12}
            lg={6}
          >
            <Text
              type="secondary"
            >
              Mostrar progresso
            </Text>

            <div>
              <Tag
                color={
                  config.showProgress
                    ? 'green'
                    : 'default'
                }
              >
                {
                  config.showProgress
                    ? 'Sim'
                    : 'Não'
                }
              </Tag>
            </div>
          </Col>

          <Col
            xs={24}
            sm={12}
            lg={6}
          >
            <Text
              type="secondary"
            >
              Mostrar pontuação
            </Text>

            <div>
              <Tag
                color={
                  config.showScore
                    ? 'green'
                    : 'default'
                }
              >
                {
                  config.showScore
                    ? 'Sim'
                    : 'Não'
                }
              </Tag>
            </div>
          </Col>

          <Col
            xs={24}
            sm={12}
            lg={6}
          >
            <Text
              type="secondary"
            >
              Avanço automático
            </Text>

            <div>
              <Tag
                color={
                  config.autoAdvance
                    ? 'green'
                    : 'default'
                }
              >
                {
                  config.autoAdvance
                    ? 'Sim'
                    : 'Não'
                }
              </Tag>
            </div>
          </Col>
        </Row>
      </Card>

      {/* ==================================================
          PERGUNTAS
      ================================================== */}

      <Card
        title={`Perguntas (${questions.length})`}
        extra={
          <Button
            type="primary"
            icon={
              <PlusOutlined />
            }
            onClick={
              handleNewQuestion
            }
          >
            Nova pergunta
          </Button>
        }
        style={{
          marginBottom:
            24,
        }}
      >
        {questions.length ===
          0 ? (
          <Empty
            description="Nenhuma pergunta cadastrada."
          />
        ) : (
          questions.map(
            (
              question,
              questionIndex,
            ) => {
              const options =
                question?.options ||
                []

              return (
                <Card
                  key={
                    question.id
                  }
                  type="inner"
                  title={
                    question.title ||
                    `Pergunta ${questionIndex +
                    1
                    }`
                  }
                  style={{
                    marginBottom:
                      16,
                  }}
                  extra={
                    <Space
                      wrap
                    >
                      {question.isActive ===
                        false ? (
                        <Tag>
                          Inativa
                        </Tag>
                      ) : (
                        <Tag
                          color="green"
                        >
                          Ativa
                        </Tag>
                      )}

                      <Button
                        size="small"
                        icon={
                          <EditOutlined />
                        }
                        onClick={() =>
                          handleEditQuestion(
                            question,
                          )
                        }
                      >
                        Editar
                      </Button>

                      <Popconfirm
                        title="Excluir pergunta?"
                        description="Esta ação não poderá ser desfeita."
                        okText="Excluir"
                        cancelText="Cancelar"
                        okButtonProps={{
                          danger:
                            true,
                        }}
                        onConfirm={() =>
                          handleDeleteQuestion(
                            question,
                          )
                        }
                      >
                        <Button
                          size="small"
                          danger
                          loading={
                            deletingQuestionId ===
                            question.id
                          }
                          icon={
                            <DeleteOutlined />
                          }
                        >
                          Excluir
                        </Button>
                      </Popconfirm>
                    </Space>
                  }
                >
                  <Paragraph
                    style={{
                      fontSize:
                        16,

                      marginBottom:
                        20,
                    }}
                  >
                    {
                      question.text
                    }
                  </Paragraph>

                  {options.map(
                    (
                      option,
                    ) => (
                      <div
                        key={
                          option.id
                        }
                        style={{
                          padding:
                            '12px 14px',

                          marginBottom:
                            8,

                          border:
                            '1px solid #e8e8e8',

                          borderRadius:
                            8,

                          display:
                            'flex',

                          alignItems:
                            'center',

                          justifyContent:
                            'space-between',

                          gap:
                            16,
                        }}
                      >
                        <div
                          style={{
                            display:
                              'flex',

                            alignItems:
                              'flex-start',

                            gap:
                              10,
                          }}
                        >
                          <Tag
                            color="blue"
                            style={{
                              margin:
                                0,
                            }}
                          >
                            {
                              option.label
                            }
                          </Tag>

                          <span>
                            {
                              option.text
                            }
                          </span>
                        </div>

                        <Tag
                          color="gold"
                          style={{
                            margin:
                              0,

                            whiteSpace:
                              'nowrap',
                          }}
                        >
                          {
                            option.points
                          }{' '}
                          {
                            Number(
                              option.points,
                            ) ===
                              1
                              ? 'ponto'
                              : 'pontos'
                          }
                        </Tag>
                      </div>
                    ),
                  )}
                </Card>
              )
            },
          )
        )}
      </Card>

      {/* ==================================================
          PERFIS DE RESULTADO
      ================================================== */}

      <Card
        title={`Perfis de Resultado (${results.length})`}
        extra={
          <Button
            type="primary"
            icon={
              <PlusOutlined />
            }
            onClick={
              handleNewResult
            }
          >
            Novo perfil
          </Button>
        }
      >
        {results.length ===
          0 ? (
          <Empty
            description="Nenhum perfil cadastrado."
          />
        ) : (
          <Row
            gutter={[
              16,
              16,
            ]}
          >
            {results.map(
              (
                result,
              ) => (
                <Col
                  key={
                    result.id
                  }
                  xs={24}
                  xl={8}
                >
                  <Card
                    type="inner"
                    title={
                      result.title
                    }
                    extra={
                      <Space
                        wrap
                      >
                        {result.isActive ===
                          false ? (
                          <Tag>
                            Inativo
                          </Tag>
                        ) : (
                          <Tag
                            color="green"
                          >
                            Ativo
                          </Tag>
                        )}

                        <Button
                          size="small"
                          icon={
                            <EditOutlined />
                          }
                          onClick={() =>
                            handleEditResult(
                              result,
                            )
                          }
                        />

                        <Popconfirm
                          title="Excluir perfil?"
                          description="Esta ação não poderá ser desfeita."
                          okText="Excluir"
                          cancelText="Cancelar"
                          okButtonProps={{
                            danger:
                              true,
                          }}
                          onConfirm={() =>
                            handleDeleteResult(
                              result,
                            )
                          }
                        >
                          <Button
                            size="small"
                            danger
                            loading={
                              deletingResultId ===
                              result.id
                            }
                            icon={
                              <DeleteOutlined />
                            }
                          />
                        </Popconfirm>
                      </Space>
                    }
                    style={{
                      height:
                        '100%',
                    }}
                  >
                    <Tag
                      color="gold"
                      style={{
                        marginBottom:
                          16,
                      }}
                    >
                      {
                        result.minScore
                      }{' '}
                      a{' '}
                      {
                        result.maxScore
                      }{' '}
                      pontos
                    </Tag>

                    {result.subtitle && (
                      <div
                        style={{
                          marginBottom:
                            8,
                        }}
                      >
                        <Text
                          strong
                        >
                          {
                            result.subtitle
                          }
                        </Text>
                      </div>
                    )}

                    {result.description && (
                      <>
                        <Text
                          strong
                        >
                          Cenário
                        </Text>

                        <Paragraph>
                          {
                            result.description
                          }
                        </Paragraph>
                      </>
                    )}

                    {result.recommendation && (
                      <>
                        <Text
                          strong
                        >
                          Recomendação
                        </Text>

                        <Paragraph>
                          {
                            result.recommendation
                          }
                        </Paragraph>
                      </>
                    )}
                  </Card>
                </Col>
              ),
            )}
          </Row>
        )}
      </Card>

      {/* ==================================================
    MODAL CONFIGURAÇÃO
================================================== */}

      <Modal
        title="Configuração Geral"
        open={
          configModalOpen
        }
        onCancel={
          handleCloseConfig
        }
        onOk={
          handleSaveConfig
        }
        confirmLoading={
          savingConfig
        }
        okText="Salvar"
        cancelText="Cancelar"
        width={900}
        destroyOnClose
      >
        <Form
          form={
            configForm
          }
          layout="vertical"
        >
          {/* ================================================
        FUNCIONAMENTO DO QUIZ
    ================================================ */}

          <Card
            title="Funcionamento do Quiz"
            size="small"
            style={{
              marginBottom: 20,
            }}
          >
            <Form.Item
              label="Modo do Quiz"
              name="mode"
              rules={[
                {
                  required: true,
                  message:
                    'Selecione o modo.',
                },
              ]}
            >
              <Select
                options={[
                  {
                    value:
                      'SCORE_PROFILE',

                    label:
                      'Pontuação + Perfil',
                  },
                  {
                    value:
                      'SCORE',

                    label:
                      'Somente pontuação',
                  },
                ]}
              />
            </Form.Item>

            <Row
              gutter={[
                24,
                24,
              ]}
            >
              <Col
                xs={24}
                md={8}
              >
                <Form.Item
                  label="Mostrar progresso"
                  name="showProgress"
                  valuePropName="checked"
                  style={{
                    marginBottom: 0,
                  }}
                >
                  <Switch
                    checkedChildren="Sim"
                    unCheckedChildren="Não"
                  />
                </Form.Item>
              </Col>

              <Col
                xs={24}
                md={8}
              >
                <Form.Item
                  label="Mostrar pontuação"
                  name="showScore"
                  valuePropName="checked"
                  style={{
                    marginBottom: 0,
                  }}
                >
                  <Switch
                    checkedChildren="Sim"
                    unCheckedChildren="Não"
                  />
                </Form.Item>
              </Col>

              <Col
                xs={24}
                md={8}
              >
                <Form.Item
                  label="Avanço automático"
                  name="autoAdvance"
                  valuePropName="checked"
                  style={{
                    marginBottom: 0,
                  }}
                >
                  <Switch
                    checkedChildren="Sim"
                    unCheckedChildren="Não"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* ================================================
        CAMPOS OBRIGATÓRIOS
    ================================================ */}

          <Card
            title="Campos Obrigatórios"
            size="small"
            style={{
              marginBottom: 20,
            }}
          >
            <Text
              type="secondary"
            >
              Defina quais dados precisam ser preenchidos
              pelo participante para este Quiz.
            </Text>

            <Row
              gutter={[
                24,
                16,
              ]}
              style={{
                marginTop: 18,
              }}
            >
              <Col
                xs={12}
                md={6}
              >
                <Form.Item
                  name="requireName"
                  label="Exigir nome"
                  valuePropName="checked"
                  style={{
                    marginBottom: 0,
                  }}
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col
                xs={12}
                md={6}
              >
                <Form.Item
                  name="requirePhone"
                  label="Exigir telefone"
                  valuePropName="checked"
                  style={{
                    marginBottom: 0,
                  }}
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col
                xs={12}
                md={6}
              >
                <Form.Item
                  name="requireEmail"
                  label="Exigir e-mail"
                  valuePropName="checked"
                  style={{
                    marginBottom: 0,
                  }}
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col
                xs={12}
                md={6}
              >
                <Form.Item
                  name="requireCpf"
                  label="Exigir CPF"
                  valuePropName="checked"
                  style={{
                    marginBottom: 0,
                  }}
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* ================================================
        REGRAS DE PARTICIPAÇÃO
    ================================================ */}

          <Card
            title="Regras de Participação"
            size="small"
          >
            <Text
              type="secondary"
            >
              Controle quando o mesmo participante poderá
              responder novamente este Quiz.
            </Text>

            <Row
              gutter={[
                24,
                16,
              ]}
              style={{
                marginTop: 18,
              }}
            >
              <Col
                xs={24}
                md={8}
              >
                <Form.Item
                  name="onePlayPerPhone"
                  label="Bloquear telefone repetido"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col
                xs={24}
                md={8}
              >
                <Form.Item
                  name="onePlayPerEmail"
                  label="Bloquear e-mail repetido"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col
                xs={24}
                md={8}
              >
                <Form.Item
                  name="onePlayPerCpf"
                  label="Bloquear CPF repetido"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="participationLimitType"
              label="Período da regra"
            >
              <Select
                options={[
                  {
                    label:
                      'Não bloquear repetição',

                    value:
                      'NONE',
                  },
                  {
                    label:
                      'Por jogo inteiro',

                    value:
                      'GAME',
                  },
                  {
                    label:
                      'Por dia',

                    value:
                      'DAY',
                  },
                  {
                    label:
                      'Por hora',

                    value:
                      'HOUR',
                  },
                  {
                    label:
                      'Após X minutos',

                    value:
                      'MINUTES',
                  },
                ]}
              />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(
                previous,
                current,
              ) =>
                previous
                  .participationLimitType !==
                current
                  .participationLimitType
              }
            >
              {({
                getFieldValue,
              }) => {
                const limitType =
                  getFieldValue(
                    'participationLimitType',
                  )

                if (
                  limitType !==
                  'MINUTES'
                ) {
                  return null
                }

                return (
                  <Form.Item
                    name="participationLimitMinutes"
                    label="Minutos para permitir nova participação"
                    rules={[
                      {
                        required:
                          true,

                        message:
                          'Informe o número de minutos.',
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      max={525600}
                      precision={0}
                      style={{
                        width: '100%',
                      }}
                      placeholder="Ex: 60"
                    />
                  </Form.Item>
                )
              }}
            </Form.Item>
          </Card>
        </Form>
      </Modal>

      {/* ==================================================
          MODAL PERGUNTA
      ================================================== */}

      <Modal
        title={
          editingQuestion
            ? 'Editar pergunta'
            : 'Nova pergunta'
        }
        open={
          questionModalOpen
        }
        onCancel={
          handleCloseQuestionModal
        }
        onOk={
          handleSaveQuestion
        }
        confirmLoading={
          savingQuestion
        }
        okText="Salvar"
        cancelText="Cancelar"
        width={850}
        destroyOnClose
      >
        <Form
          form={
            questionForm
          }
          layout="vertical"
        >
          <Form.Item
            label="Título"
            name="title"
            rules={[
              {
                required:
                  true,

                whitespace:
                  true,

                message:
                  'Informe o título da pergunta.',
              },
            ]}
          >
            <Input
              placeholder="Ex: 1. O Volume Diário"
              maxLength={
                200
              }
            />
          </Form.Item>

          <Form.Item
            label="Pergunta"
            name="text"
            rules={[
              {
                required:
                  true,

                whitespace:
                  true,

                message:
                  'Informe o texto da pergunta.',
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Digite a pergunta..."
            />
          </Form.Item>

          <Form.Item
            label="Pergunta ativa"
            name="isActive"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <div
            style={{
              marginTop:
                24,

              marginBottom:
                12,

              display:
                'flex',

              justifyContent:
                'space-between',

              alignItems:
                'center',
            }}
          >
            <Text strong>
              Alternativas
            </Text>

            <Text
              type="secondary"
            >
              Mínimo de 2 alternativas
            </Text>
          </div>

          <Form.List
            name="options"
            rules={[
              {
                validator:
                  async (
                    _,
                    options,
                  ) => {
                    if (
                      !options ||
                      options.length <
                      2
                    ) {
                      throw new Error(
                        'Cadastre pelo menos 2 alternativas.',
                      )
                    }
                  },
              },
            ]}
          >
            {(
              fields,
              {
                add,
                remove,
              },
              {
                errors,
              },
            ) => (
              <>
                {fields.map(
                  (
                    field,
                    index,
                  ) => {
                    const label =
                      String.fromCharCode(
                        65 +
                        index,
                      )

                    return (
                      <Card
                        key={
                          field.key
                        }
                        size="small"
                        style={{
                          marginBottom:
                            12,
                        }}
                      >
                        <Row
                          gutter={[
                            12,
                            12,
                          ]}
                          align="middle"
                        >
                          <Col
                            flex="50px"
                          >
                            <Tag
                              color="blue"
                              style={{
                                fontSize:
                                  16,

                                padding:
                                  '5px 10px',

                                marginTop:
                                  29,
                              }}
                            >
                              {
                                label
                              }
                            </Tag>
                          </Col>

                          <Col
                            flex="auto"
                          >
                            <Form.Item
                              label="Alternativa"
                              name={[
                                field.name,
                                'text',
                              ]}
                              rules={[
                                {
                                  required:
                                    true,

                                  whitespace:
                                    true,

                                  message:
                                    'Informe a alternativa.',
                                },
                              ]}
                              style={{
                                marginBottom:
                                  0,
                              }}
                            >
                              <Input
                                placeholder={`Texto da alternativa ${label}`}
                              />
                            </Form.Item>
                          </Col>

                          <Col
                            flex="130px"
                          >
                            <Form.Item
                              label="Pontos"
                              name={[
                                field.name,
                                'points',
                              ]}
                              rules={[
                                {
                                  required:
                                    true,

                                  message:
                                    'Informe os pontos.',
                                },
                              ]}
                              style={{
                                marginBottom:
                                  0,
                              }}
                            >
                              <InputNumber
                                min={0}
                                precision={
                                  0
                                }
                                style={{
                                  width:
                                    '100%',
                                }}
                              />
                            </Form.Item>
                          </Col>

                          <Col
                            flex="50px"
                          >
                            <Button
                              danger
                              type="text"
                              icon={
                                <DeleteOutlined />
                              }
                              disabled={
                                fields.length <=
                                2
                              }
                              onClick={() =>
                                remove(
                                  field.name,
                                )
                              }
                              style={{
                                marginTop:
                                  29,
                              }}
                            />
                          </Col>
                        </Row>
                      </Card>
                    )
                  },
                )}

                <Button
                  type="dashed"
                  block
                  icon={
                    <PlusOutlined />
                  }
                  onClick={() =>
                    add({
                      text:
                        '',

                      points:
                        fields.length +
                        1,

                      isCorrect:
                        false,
                    })
                  }
                >
                  Adicionar alternativa
                </Button>

                <Form.ErrorList
                  errors={
                    errors
                  }
                />
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* ==================================================
          MODAL PERFIL DE RESULTADO
      ================================================== */}

      <Modal
        title={
          editingResult
            ? 'Editar perfil de resultado'
            : 'Novo perfil de resultado'
        }
        open={
          resultModalOpen
        }
        onCancel={
          handleCloseResultModal
        }
        onOk={
          handleSaveResult
        }
        confirmLoading={
          savingResult
        }
        okText="Salvar"
        cancelText="Cancelar"
        width={800}
        destroyOnClose
      >
        <Form
          form={
            resultForm
          }
          layout="vertical"
        >
          <Row
            gutter={[
              16,
              0,
            ]}
          >
            <Col
              xs={24}
              md={16}
            >
              <Form.Item
                label="Título do perfil"
                name="title"
                rules={[
                  {
                    required:
                      true,

                    whitespace:
                      true,

                    message:
                      'Informe o título do perfil.',
                  },
                ]}
              >
                <Input
                  placeholder="Ex: Consumo Consciente"
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              md={8}
            >
              <Form.Item
                label="Perfil ativo"
                name="isActive"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Sim"
                  unCheckedChildren="Não"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Subtítulo"
            name="subtitle"
          >
            <Input
              placeholder="Ex: Seu consumo está equilibrado"
            />
          </Form.Item>

          <Row
            gutter={[
              16,
              0,
            ]}
          >
            <Col
              xs={24}
              md={12}
            >
              <Form.Item
                label="Pontuação mínima"
                name="minScore"
                rules={[
                  {
                    required:
                      true,

                    message:
                      'Informe a pontuação mínima.',
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  precision={0}
                  style={{
                    width:
                      '100%',
                  }}
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              md={12}
            >
              <Form.Item
                label="Pontuação máxima"
                name="maxScore"
                dependencies={[
                  'minScore',
                ]}
                rules={[
                  {
                    required:
                      true,

                    message:
                      'Informe a pontuação máxima.',
                  },
                  (
                    {
                      getFieldValue,
                    },
                  ) => ({
                    validator(
                      _,
                      value,
                    ) {
                      const minimum =
                        Number(
                          getFieldValue(
                            'minScore',
                          ) ??
                          0,
                        )

                      if (
                        value ===
                        undefined ||
                        value ===
                        null ||
                        Number(
                          value,
                        ) >=
                        minimum
                      ) {
                        return Promise.resolve()
                      }

                      return Promise.reject(
                        new Error(
                          'A pontuação máxima deve ser maior ou igual à mínima.',
                        ),
                      )
                    },
                  }),
                ]}
              >
                <InputNumber
                  min={0}
                  precision={0}
                  style={{
                    width:
                      '100%',
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Cenário / descrição"
            name="description"
          >
            <TextArea
              rows={5}
              placeholder="Explique o cenário deste perfil..."
              showCount
              maxLength={2000}
            />
          </Form.Item>

          <Form.Item
            label="Recomendação"
            name="recommendation"
          >
            <TextArea
              rows={5}
              placeholder="Informe a recomendação exibida no resultado..."
              showCount
              maxLength={2000}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default QuizSettingsPage